// AppShell.tsx (or RootLayout)
import Loader from "@/components/ui/Loader/Loader";
import { IMPERSONATION_MUTATION_KEY } from "@/hooks/user-management/useUserManagementMutations";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { LoadingOverlay } from "@mantine/core";
import { useMutationState } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ImpersonationBanner } from "../ImpersonationBanner/ImpersonationBanner";

const overlayLoaderProps = {
  children: <Loader withContainer={false} size="medium" />,
};

function AppLayout({ children }: { children: ReactNode }) {
  const isLoading = useRouterState({
    select: (state) => state.isLoading,
  });
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);

  const isImpersonatingLoading = useMutationState({
    filters: { mutationKey: IMPERSONATION_MUTATION_KEY, status: "pending" },
    select: (mutation) => mutation.state.status === "pending",
  }).some(Boolean);

  if (isImpersonatingLoading) {
    return (
      <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} loaderProps={overlayLoaderProps} />
    );
  }

  return (
    <div
      data-slot={"root"}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        backgroundColor: "var(--neutral-500)",
      }}
    >
      {isImpersonating && <ImpersonationBanner />}
      <LoadingOverlay
        visible={isLoading}
        overlayProps={{ blur: 2 }}
        loaderProps={overlayLoaderProps}
      />
      <div data-slot={"app-layout-root"} style={{ flex: 1, minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

export default AppLayout;
